import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { Dialog, OperationState, UserServerSettings, NotificationPreference } from '../../../store/appState';
import { App, SignInState } from '../../../app';
import { ResourceStrings } from '../../../ResourceStrings';
import { AuthenticationMessages } from '../Submission/Notifications';
import { ChildrenProps } from '../../../utils/types';
import styled from 'styled-components/macro';
import * as Actions from '../../../store/actions';
import Button from '../../shared/Button';
import ProfileAttachmentList from './ProfileAttachmentList';
import Accordion from '../../shared/Accordion';
import { Icon } from '@fluentui/react/lib/Icon';
import DeleteAccountDialog from './DeleteAccountDialog';
import { ExportRestrictStatusType, RequestType } from '../../../constants/gdprRequests';
import ArchiveZipList from './ArchiveZipList';
import { AppPaths } from '../../../constants/urls';
import { Link, useParams } from 'react-router-dom';
import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import Avatar from '../../shared/Avatar';
import { instance } from '../../../config/serverConfig';

export interface ProfileViewProps {
}

interface ProfileViewUrlParams {
    userId: string;
}

const emailOptions: IChoiceGroupOption[] = [
    { key: NotificationPreference.Off.toString(), text: 'None' },
    { key: NotificationPreference.Instant.toString(), text: 'Instant' },
    { key: NotificationPreference.Daily.toString(), text: 'Daily' },
    { key: NotificationPreference.Weekly.toString(), text: 'Weekly' },
];

export default function ProfileView(props: ProfileViewProps): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);
    const vsid = useSelector((state: RootState) => App.getVsid(state.appState.settings.template)); // from the currently signed in user
    const isInReadOnlyMode = useSelector((state: RootState) => state.appState.isInReadOnlyMode);
    const { userId: vsidOrPrincipalIdFromUrl }: ProfileViewUrlParams = useParams();
    const profileMy = "my";
    const notificationSettings = useSelector((state: RootState) => state.appState.userServerSettings?.notifications);

    const selectedNotificationValue: string = !notificationSettings
        ? null
        : notificationSettings.preference.toString();

    useEffect(() => {
        Actions.setDialog(Dialog.none);
    }, []);

    useEffect(() => {
        if (vsidOrPrincipalIdFromUrl !== profileMy) { // if user id is vsid or principal id
            Actions.loadUserProfile(vsidOrPrincipalIdFromUrl);
            return;
        }

        // vsidOrPrincipalIdFromUrl is "my" for URLs like ".../user/my/profile"
        if (vsid && vsid !== "unknown") {
            Actions.loadUserProfile(vsid);
        }
    }, [vsidOrPrincipalIdFromUrl, vsid]);

    useEffect(() => {
        if (vsid && vsid !== "unknown") {
            Actions.getArchiveStatus();
            Actions.getUserServerSettings();
        }
    }, [vsid]);

    const signinState: number = state.signInState;
    const loadState: OperationState = state.userProfileLoadState;

    const isCurrentUserProfile: boolean = (state.userVsid === state.userProfile?.vsid);
    const isSuspended: boolean = state.userProfile?.isSuspended === true;

    const onViewAttachmentsClick = (): void => {
        App.telemetry.postBusinessEvent("viewattachments-clicked", {});
        Actions.viewAttachments();
    }

    const onCreateArchive = (): void => {
        Actions.createArchive();
    }

    const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
    const onDeleteAccountClick = (evt): void => {
        evt.preventDefault();
        setShowDeleteAccountDialog(true);
    }
    const onCloseDeleteDialog = (): void => {
        setShowDeleteAccountDialog(false);
    }

    const onNotificationOptionsChange = (ev: React.SyntheticEvent<HTMLElement>, option: IChoiceGroupOption): void => {
        const newSettings: UserServerSettings = JSON.parse(JSON.stringify(state.userServerSettings)) ?? {};
        const selectedValue = option.key;
        newSettings.notifications = {
            preference: selectedValue ? parseInt(selectedValue) : NotificationPreference.Off,
        };

        Actions.setUserServerSettingsLocally(newSettings);
        Actions.postUserServerSettings(newSettings);
    };

    return <Wrapper data-cy="rap-submit-post-view">
        {isCurrentUserProfile && <AuthenticationMessages
            signinState={signinState}
            signInErrorMessage={state.signInErrorMessage}
            render={(props: ChildrenProps) => <PageContent>{props.children}</PageContent>} />
        }

        {loadState === OperationState.Failed && <PageContent>
            <div>Error loading Profile and Preferences</div>
            <div>Please try signing out and signing back in. If you recently added your first comment, problem, or suggestion to Developer Community, please wait 15 minutes and try opening Profile and Preferences again.</div>
        </PageContent>}

        {state.archiveStatusLoadState === OperationState.Failed && <PageContent>
            <div>Error loading your archived files information.</div>
            <pre>{state.archiveStatusErrorMessage}</pre>
        </PageContent>}

        {(!isCurrentUserProfile || signinState === SignInState.Success) && <>
            {loadState === OperationState.Executing && <PageContent>
                <div>{ResourceStrings.loading()}</div>
            </PageContent>}

            {loadState === OperationState.Success && <>
                {isCurrentUserProfile && state.archiveCurrentStatus != null && state.archiveCurrentStatus.RequestType === RequestType.Delete && <PageContent>
                    <div>You requested to delete your Developer Community data. Your data and account will be deleted.</div>
                </PageContent>}

                {(!isCurrentUserProfile || (state.archiveCurrentStatus != null && state.archiveCurrentStatus.RequestType !== RequestType.Delete)) &&
                    <PageContent>
                        <div className="background-panel">
                            <PanelWrapper>
                                {/* User profile panel */}
                                <Panel className="profile-panel flex-container columns">
                                    <Avatar
                                        className='avatar-logo'
                                        name={isCurrentUserProfile ? state.userName : state.userProfile.userName}
                                        imageUrl={isCurrentUserProfile ? state.userLogo : state.userProfile.userPhoto}
                                    />

                                    <div className="large-space">{isCurrentUserProfile ? state.userName : state.userProfile.userName}</div>

                                    {/* Show email from DB. Server will only return it for current user or moderators/admins */}
                                    {state.userProfile?.email && <div>{state.userProfile?.email}</div>}

                                    <UserStatusWrapper>
                                        Account status: {isSuspended ? <b>Suspended</b> : <>Active</>}
                                    </UserStatusWrapper>

                                    {isCurrentUserProfile && <div className="vertical-space">

                                        {(state.userServerSettingsLoadState === OperationState.Executing || state.userServerSettingsLoadState === OperationState.NotStarted) && <>
                                            <div className="vertical-space">{ResourceStrings.loading()}</div>
                                        </>}

                                        {/* Notification options */}
                                        {(state.userServerSettingsLoadState === OperationState.Success || state.userServerSettingsLoadState === OperationState.Failed) && <>
                                            <HeaderWrapper css="border-top: .1rem solid var(--color-on-surface-lighter); padding-top: 1.5rem">
                                                <Icon className="left-icon inline" iconName="WorkItemAlert" ></Icon>
                                                <span className="title-instruction semibold">Email Notifications</span>
                                            </HeaderWrapper>

                                            <ChoiceGroup defaultSelectedKey="instant" options={emailOptions} selectedKey={selectedNotificationValue} onChange={onNotificationOptionsChange} required={true} />
                                        </>}

                                        {state.userServerSettingsLoadState === OperationState.Failed && state.userServerSettingsSaveState !== OperationState.Success && <>
                                            <NotificationUpdateStatus className="error">Error while loading settings.</NotificationUpdateStatus>
                                        </>}

                                        {state.userServerSettingsSaveState === OperationState.Executing && <>
                                            <NotificationUpdateStatus>Updating settings...</NotificationUpdateStatus>
                                        </>}
                                        {state.userServerSettingsSaveState === OperationState.Failed && <>
                                            <NotificationUpdateStatus className="error">Error while saving settings</NotificationUpdateStatus>
                                        </>}
                                        {state.userServerSettingsSaveState === OperationState.Success && <>
                                            <NotificationUpdateStatus>Settings were updated successfully.</NotificationUpdateStatus>
                                        </>}

                                        <div className="large-space" css="border-top: .1rem solid var(--color-on-surface-lighter);">
                                            <a className="vertical-space block" href="https://app.vsaex.visualstudio.com/go/profile" target="_blank" rel="noopener noreferrer">Edit profile <Icon className="small-icon" iconName="ChevronRight" /></a>
                                            <Link to={AppPaths.myactivity} className="vertical-space block">Show my activity <Icon className="small-icon" iconName="ChevronRight" /></Link>
                                        </div>
                                    </div>}
                                </Panel>

                                {/* Privacy panel, only visible for External instance */}
                                {!instance.isInternal && isCurrentUserProfile && <Panel className="privacy-panel">
                                    <HeaderWrapper>
                                        <Icon className="left-icon inline" iconName="ContactCardSettingsMirrored"></Icon>
                                        <span className="title-instruction semibold">Privacy</span>
                                    </HeaderWrapper>

                                    <div className="privacy-text">See and manage <a href="https://aka.ms/DevComdataprivacy" target="_blank" rel="noopener noreferrer">your data</a> in your Developer Community account. Your data includes your profile information, attachments, and system information collected when you submitted feedback. <b>See the <a href="https://aka.ms/DevComprivacystatement" target="_blank" rel="noopener noreferrer">Microsoft Privacy Statement</a> for more information.</b></div>

                                    {(state.archiveCurrentStatus != null && state.archiveCurrentStatus.Status !== ExportRestrictStatusType.NotStarted && state.archiveCurrentStatus.Status !== ExportRestrictStatusType.Expired) &&
                                        <div className="large-space">
                                            <div className="flex-container divider padding">
                                                <div className="large-col semibold">Archive Status</div>
                                                <div className="small-col semibold">Created On</div>
                                                <div className="small-col semibold">Available Until</div>
                                            </div>
                                            <div className="flex-container divider padding">
                                                {(state.archiveCurrentStatus.Status === ExportRestrictStatusType.Received ||
                                                    state.archiveCurrentStatus.Status === ExportRestrictStatusType.InProgress) && <>
                                                        <div>We are preparing an archive of your data. Please note that archives may take hours or possibly days to create. <b>Check your email or check back here to see if your archive is complete.</b></div>
                                                    </>
                                                }
                                                {state.archiveCurrentStatus.Status === ExportRestrictStatusType.Completed && <>
                                                    <div className="large-col">
                                                        <div>Ready to download</div>
                                                        <ArchiveZipList></ArchiveZipList>
                                                    </div>
                                                    <div className="small-col">{App.ParseDateTimeToLocal(state.archiveCurrentStatus.CreatedDate)}</div>
                                                    <div className="small-col">{App.ParseDateTimeToLocal(state.archiveCurrentStatus.ExpireDate)}</div>
                                                </>
                                                }
                                            </div>
                                        </div>
                                    }

                                    <Button className="block large-space" buttonType="normal" disabled={isInReadOnlyMode} onClick={onCreateArchive}>Create an archive</Button>

                                    {!isInReadOnlyMode && <a role="button" className="delete-button block vertical-space" href={"Delete my data and account"} onClick={onDeleteAccountClick}>Delete my data and account</a>}

                                    <Accordion
                                        className="privacy-accordion large-space"
                                        title={<div>Show my attachments</div>}
                                        onExpandedToggle={(isExpanded) => { if (isExpanded) { onViewAttachmentsClick() } }}
                                        expanderLocation="left"
                                    >
                                        <div className="padding">
                                            {state.profileAttachmentsLoadState === OperationState.Executing && <div tabIndex={0}>{ResourceStrings.loading()} </div>}

                                            {state.profileAttachmentsLoadState === OperationState.Success && <ProfileAttachmentList />}

                                            {state.profileAttachmentsLoadState === OperationState.Failed && <div tabIndex={0}>
                                                {`Error getting attactments`}
                                            </div>}
                                        </div>
                                    </Accordion>
                                </Panel>}
                            </PanelWrapper>

                            {isCurrentUserProfile && <DeleteAccountDialog
                                hidden={!showDeleteAccountDialog}
                                onClose={onCloseDeleteDialog}
                            />}
                        </div>
                    </PageContent>
                }
            </>
            }
        </>
        }
    </Wrapper >
}

const Wrapper = styled.div`
    background-color: var(--color-background);
`;

const PageContent = styled.main`
    min-height: 60vh;

    margin-left: auto;
    margin-right: auto;
    padding: 2rem;

    >* + * {
        margin-top: 2.5rem;
    }

    .title-instruction {
        font-size: 1.8rem;
        line-height: 2.4rem;
        font-weight: 350;
    }

    .block {
        display: block;
    }

    .flex-container {
        box-sizing: border-box;
        display: flex;
        flex-wrap: wrap;
    }

    .columns {
        flex-direction: column;
    }

    .vertical-space {
        margin-top: 1.5rem;
    }

    .large-space {
        margin-top: 3rem;
    }

    .small-icon {
        font-size: 0.6em;
    }
`;

const PanelWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    width: auto;
    justify-content: left;

    .small-panel {
        flex: 1;
    }

    .large-panel {
        flex: 2;
    }
`;

const Panel = styled.section`
    min-width: 28.5rem;
    min-height: 28rem;

    margin: .9rem; // TODO: Is this value extracted to a central place?
    padding: 2rem 2.3rem 2rem 2.3rem;

    position: relative;

    background: var(--color-surface);
    box-shadow: 0px 0.3px 0.9px rgba(0, 0, 0, 0.108), 0px 1.6px 3.6px rgba(0, 0, 0, 0.132);
    border-radius: 2px;

    .profile-panel {
        flex: 1;

        .align-bottom {
            margin-top: auto;
        }
    }

    .ms-Checkbox-text {
        font-size: var(--font-size-default);
    }

    &.privacy-panel {
        flex: 3;
        padding-bottom: 3rem;

        .privacy-accordion {
            >.accordion-header {
                border-top: .1rem solid var(--color-on-surface-lighter);
                border-bottom: .1rem solid var(--color-on-surface-lighter);
                padding-left: 0;
            }
        }

        .privacy-text {
            margin-top: 1.5rem;
        }

        .divider {
            border-bottom: .1rem solid var(--color-on-surface-lighter);
        }

        .padding {
            padding: 1rem 0rem;
        }

        .large-col {
            flex: 4;
        }

        .small-col {
            flex: 1;
            margin: auto;
        }

        .delete-button {
            width: fit-content;
        }
    }

    .avatar-logo {
        border-radius: 52%;
        box-sizing: 52%;
        width: 10rem;
        height: 10rem;
        font-size: 3.6rem;
    }
`;

const HeaderWrapper = styled.div`
    margin-top: 1.5rem;

    .left-icon {
        margin-right: 1.2rem;
    }

    .inline {
        display: inline-block;
    }
`;

const NotificationUpdateStatus = styled.div`
    border-top: solid .1rem #eeeeee;
    margin-top: 1.5rem;
    padding-top: .5rem;
`;

const UserStatusWrapper = styled.div`
    margin-top: 1.5rem;
    border-radius: 4px;
`;
